import { type ReactNode } from 'react';

import {
  Avatar,
  AvatarGroup,
  Box,
  ButtonBase,
  Divider,
  Card,
  IconButton,
  Stack,
  Typography,
  alpha,
} from '@mui/material';

import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';

import ClaimParts from '@/karehero-app/tell-us-once/intake/ClaimParts';
import { type ClaimPart } from '@/karehero-app/tell-us-once/intake/content';

/**
 * Something given back, from us rather than from Keni.
 *
 * Onboarding 1.1, from the warming-up doc: the flow asks nine times before it
 * gives anything back, so a few answers in it hands something over. Drawn as
 * the summary's card (the sparkle, the overline label, the body) because it is
 * the same kind of thing: something we produced and are showing them, not a
 * line in the conversation.
 *
 * One component for every give-back: the value card after the fork, the care
 * experts after the names, and the work shown while the reading runs, which
 * passes its step list as children.
 */

const FromUsCard = ({
  label,
  body,
  note,
  people,
  items,
  checked,
  claim,
  claimView,
  found,
  pinned = false,
  sentence = false,
  onEdit,
  onItem,
  sparkle = true,
  asHomeCard = false,
  children,
}: {
  /**
   * The label and body in the type Home's own cards use (h4 and body1, as on
   * Financial status), for a card that sits among them.
   */
  asHomeCard?: boolean;
  /** The label as a line in sentence case, rather than an overline tag. */
  sentence?: boolean;
  /**
   * The coral sparkle before the label, which says the card came from the AI.
   * Off for a card that's just a way in, like Check your eligibility on Home.
   */
  sparkle?: boolean;
  /** A pencil in the top right that opens the lines for editing. */
  onEdit?: () => void;
  /**
   * Each checklist item is a way in, with a chevron: tapping one starts what
   * it names, like its own conversation. Checklists only.
   */
  onItem?: (index: number) => void;
  label: string;
  body?: string;
  /** One line each, under the body. */
  items?: string[];
  /**
   * Draw the items as a checklist, with this many ticked: done ones ticked
   * green, the next one marked coral, the rest an empty circle. -1 shows the
   * list before anything has started, with nothing marked as next.
   */
  checked?: number;
  /** A claim in its parts, with chips between them. See ClaimParts. */
  claim?: ClaimPart[];
  claimView?: 'all' | number;
  /** What was found for each item, shown under it once it is ticked. */
  found?: string[];
  /** Held at the top of the chat: a pin in the top right says so. */
  pinned?: boolean;
  note?: string;
  /** First names. Initials stand in for the team photo until there is one. */
  people?: string[];
  children?: ReactNode;
}) => (
  <Card
    sx={(theme) => ({
      p: 2,
      // Pinned, it is held over the chat while messages scroll under it, so it
      // lifts off them with a soft shadow. The one exception to no elevation:
      // nothing else in the chat sits above anything else.
      boxShadow: pinned
        ? `0 6px 16px ${alpha(theme.palette.text.primary, 0.12)}`
        : 'none',
      transition: 'box-shadow 240ms',
    })}
  >
    <Stack spacing={1.5}>
      <Stack sx={{ alignItems: 'center' }} direction='row' spacing={0.75}>
        {sparkle && (
          <Box sx={{ color: 'custom.coral.600', display: 'flex' }}>
            <Icon icon={'sparkles' as never} fontSize={12} />
          </Box>
        )}
        <Typography
          // Takes the row, so a pencil after it sits in the top right.
          sx={{ flex: 1, color: sentence ? 'text.primary' : 'text.secondary' }}
          variant={asHomeCard ? 'h4' : sentence ? 'subtitle1' : 'overline'}
        >
          {label}
        </Typography>
        {pinned && (
          <Box
            sx={{ color: 'text.secondary', display: 'flex' }}
            aria-label='Pinned'
            role='img'
          >
            <Icon icon={'thumbtack' as never} type='fas' fontSize={14} />
          </Box>
        )}
        {onEdit && (
          <IconButton aria-label='Edit' size='small' onClick={onEdit}>
            <Icon icon={'pencil' as never} fontSize={14} />
          </IconButton>
        )}
      </Stack>

      {(body || people) && (
        <Stack spacing={1.5}>
          {people && (
            <AvatarGroup max={3} sx={{ alignSelf: 'flex-start' }}>
              {people.map((name) => (
                <Avatar key={name} alt={name} sx={{ width: 28, height: 28 }}>
                  {name[0]}
                </Avatar>
              ))}
            </AvatarGroup>
          )}
          {body && (
            <Typography
              sx={{ color: 'text.primary' }}
              variant={asHomeCard ? 'body1' : 'body2'}
            >
              {body}
            </Typography>
          )}
        </Stack>
      )}

      {claim && <ClaimParts parts={claim} initialView={claimView} />}

      {items && checked === undefined && (
        <Stack spacing={1}>
          {items.map((item) => (
            // Body size in neutral 600: a step softer than charcoal and still
            // 6.1:1 on the card.
            <Typography
              key={item}
              sx={{ color: 'custom.neutral.600' }}
              variant='body1'
            >
              {item}
            </Typography>
          ))}
        </Stack>
      )}

      {items && checked !== undefined && (
        // Tappable, each row is a list row: 48px at least, a rule between, so
        // every one is a target of its own rather than a line of text.
        <Stack
          spacing={onItem ? 0 : 1}
          divider={onItem ? <Divider flexItem /> : undefined}
        >
          {items.map((item, i) => (
            <Stack
              key={item}
              sx={{
                alignItems: 'center',
                width: '100%',
                textAlign: 'left',
                ...(onItem && { minHeight: 48, py: 1 }),
              }}
              direction='row'
              spacing={1}
              {...(onItem
                ? { component: ButtonBase, onClick: () => onItem(i) }
                : {})}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexShrink: 0,
                  color:
                    i < checked
                      ? 'success.main'
                      : i === checked
                        ? 'custom.coral.600'
                        : 'text.disabled',
                }}
              >
                <Icon
                  icon={
                    (i < checked
                      ? 'circle-check'
                      : i === checked
                        ? 'circle-dot'
                        : 'circle') as never
                  }
                  fontSize={14}
                />
              </Box>
              <Box sx={{ minWidth: 0, flex: onItem ? 1 : undefined }}>
                <Typography
                  sx={{
                    // Neutral 600 for the ones still to come: text.secondary is
                    // 3.3:1 on the card and fails.
                    color: i <= checked ? 'text.primary' : 'custom.neutral.600',
                  }}
                  variant='body2'
                >
                  {item}
                </Typography>
                {/* What we took for it, once it is ticked: seeing it as it is
                    taken is the first chance to spot it is wrong. */}
                {i < checked && found?.[i] && (
                  <Typography
                    sx={{ color: 'custom.neutral.600' }}
                    variant='caption'
                  >
                    {found[i]}
                  </Typography>
                )}
              </Box>
              {onItem && (
                <Box
                  sx={{ display: 'flex', flexShrink: 0, color: 'text.primary' }}
                >
                  <Icon icon={'chevron-right' as never} fontSize={12} />
                </Box>
              )}
            </Stack>
          ))}
        </Stack>
      )}

      {children}

      {note && (
        <Typography sx={{ color: 'text.secondary' }} variant='caption'>
          {note}
        </Typography>
      )}
    </Stack>
  </Card>
);

FromUsCard.displayName = 'FromUsCard';
export default FromUsCard;
